import 'react-native-gesture-handler';
import 'intl';
import 'intl/locale-data/jsonp/nl-NL';
import 'moment/locale/nl';

import {StatusBar} from 'expo-status-bar';
import React, {useEffect} from 'react';
import {View} from 'react-native';
import {NavigationContainer} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import * as Linking from 'expo-linking';
import {
  configureFonts,
  MD2LightTheme as DefaultTheme,
  Provider as PaperProvider,
} from 'react-native-paper';

import {
  useFonts,
  Roboto_100Thin,
  Roboto_300Light,
  Roboto_400Regular,
  Roboto_500Medium,
  Roboto_700Bold,
  Roboto_900Black,
} from '@expo-google-fonts/roboto';

import moment from 'moment';

import {Text} from './components/controls';
import withSessionState from './components/hoc/with-session-state';
import {useDispatch} from './lib/hooks';
import {addJobReset} from './actions';
import config from './configuration';

import {BookingFlow, ConfirmationFlow, PickupFlow, OverviewFlow} from './flows';

moment.locale('nl');

const Stack = createStackNavigator();

const prefix = Linking.makeUrl('/');
const routes = {
  screens: {
    Booking: {
      path: '',
      screens: {
        Intro: ':dealer_id',
        Pay: ':request_id/Pay',
        Complete: ':request_id/Complete',
        Confirm: ':dealer_id/Confirm',
      },
    },
    Confirm: {
      path: 'Confirm',
      screens: {
        Index: ':request_id',
        Pay: ':request_id/Pay',
        Complete: ':request_id/Complete',
        Thanks: ':request_id/Thanks',
        Chat: ':request_id/Chat',
        Details: ':request_id/Details',
      },
    },
    Pickup: {
      path: 'Pickup',
      screens: {
        Chat: ':request_id',
        Pay: ':request_id/Pay',
        Complete: ':request_id/Complete',
      },
    },
    Overview: {
      path: 'Overview',
      screens: {
        Chat: ':request_id',
        Pay: ':request_id/Pay',
        Complete: ':request_id/Complete',
        Details: ':request_id/Details',
      },
    },
  },
};

const theme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#231fda',
    background: '#ffffff',
    text: '#49494a',
  },
  fonts: configureFonts({
    config: {
      web: {
        regular: {
          fontFamily: 'Roboto_400Regular',
          fontWeight: '400',
        },
        medium: {
          fontFamily: 'Roboto_500Medium',
          fontWeight: '500',
        },
      },
      native: {
        regular: {
          fontFamily: 'Roboto_400Regular',
          fontWeight: '400',
        },
        medium: {
          fontFamily: 'Roboto_500Medium',
          fontWeight: '500',
        },
      },
    },
    isV3: false,
  }),
};

const App = () => {
  const dispatch = useDispatch();

  let [fontsLoaded] = useFonts({
    Roboto_100Thin,
    Roboto_300Light,
    Roboto_400Regular,
    Roboto_500Medium,
    Roboto_700Bold,
    Roboto_900Black,
  });

  const linking = {
    prefixes: [prefix],
    config: routes,
  };

  useEffect(() => {
    try {
      if (window?.localStorage) {
        window.localStorage.clear();
      }
    } catch {}

    if (config.development) {
      // Don't reset the job for development purposes.
      return;
    }

    dispatch(addJobReset());
  }, [dispatch]);

  if (!fontsLoaded) {
    return <View />;
  }

  return (
    <PaperProvider theme={theme}>
      <StatusBar />
      <NavigationContainer
        linking={linking}
        fallback={<Text>Loading...</Text>}
        documentTitle={{
          formatter: (options, route) => `Bahnkick`,
        }}>
        <Stack.Navigator>
          <Stack.Screen
            name="Booking"
            options={{
              headerShown: false,
            }}
            component={BookingFlow}
          />
          <Stack.Screen
            name="Confirm"
            options={{
              headerShown: false,
            }}
            component={ConfirmationFlow}
          />
          <Stack.Screen
            name="Pickup"
            options={{
              headerShown: false,
            }}
            component={PickupFlow}
          />
          <Stack.Screen
            name="Overview"
            options={{
              headerShown: false,
            }}
            component={OverviewFlow}
          />
        </Stack.Navigator>
      </NavigationContainer>
    </PaperProvider>
  );
};

export default withSessionState(App);
